import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import PlatformTenantSubscriptionPanel from './PlatformTenantSubscriptionPanel';

const { Title, Text } = Typography;

const TENANT_STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'INACTIVE', value: 'INACTIVE' },
  { label: 'SUSPENDED', value: 'SUSPENDED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pt-BR');
};

const PlatformTenantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSlugInput, setDeleteSlugInput] = useState('');
  const [deleteBlockMessage, setDeleteBlockMessage] = useState('');
  const [deleteBlockCounts, setDeleteBlockCounts] = useState(null);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [adminCreateError, setAdminCreateError] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [adminFormValues, setAdminFormValues] = useState({
    full_name: '',
    email: '',
    temporary_password: '',
  });
  const [formValues, setFormValues] = useState({
    tenant_status: undefined,
    public_signup_enabled: false,
  });

  const loadTenantDetail = useCallback(async () => {
    if (!id) {
      setError('Tenant invalido.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/api/platform-admin/tenants/${id}`);
      const tenantData = response.data?.tenant || null;
      const subscriptionData = response.data?.subscription || null;

      setTenant(tenantData);
      setSubscription(subscriptionData);
      setFormValues({
        tenant_status: tenantData?.status || undefined,
        public_signup_enabled: Boolean(tenantData?.public_signup_enabled),
      });
    } catch (requestError) {
      console.error(requestError);
      setError('Nao foi possivel carregar os dados do tenant.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTenantDetail();
  }, [loadTenantDetail]);

  const loadTenantAdmins = useCallback(async () => {
    if (!id) {
      return;
    }

    setAdminsLoading(true);
    setAdminsError('');

    try {
      const response = await api.get(`/api/platform-admin/tenants/${id}/admin-users`);
      setTenantAdmins(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      console.error(requestError);
      setAdminsError('Nao foi possivel carregar os admins do tenant.');
      setTenantAdmins([]);
    } finally {
      setAdminsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTenantAdmins();
  }, [loadTenantAdmins]);

  const initialValues = useMemo(
    () => ({
      tenant_status: tenant?.status || undefined,
      public_signup_enabled: Boolean(tenant?.public_signup_enabled),
    }),
    [tenant],
  );

  const hasChanged = (field) => {
    return formValues[field] !== initialValues[field];
  };

  const handleSave = async () => {
    if (!id) {
      return;
    }

    const payload = {};

    if (hasChanged('tenant_status') && formValues.tenant_status) {
      payload.tenant_status = formValues.tenant_status;
    }

    if (hasChanged('public_signup_enabled')) {
      payload.public_signup_enabled = Boolean(formValues.public_signup_enabled);
    }

    if (Object.keys(payload).length === 0) {
      message.info('Nenhuma alteracao para salvar.');
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      await api.patch(`/api/platform-admin/tenants/${id}`, payload);
      message.success('Tenant atualizado com sucesso.');
      await loadTenantDetail();
    } catch (requestError) {
      console.error(requestError);
      const detail = requestError?.response?.data?.detail;
      setSaveError(detail || 'Nao foi possivel salvar as alteracoes.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteSlugInput('');
    setDeleteBlockMessage('');
    setDeleteBlockCounts(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) {
      return;
    }

    setDeleteModalOpen(false);
    setDeleteSlugInput('');
    setDeleteBlockMessage('');
    setDeleteBlockCounts(null);
  };

  const handleDeleteTenant = async () => {
    if (!id || !tenant?.slug) {
      return;
    }

    setDeleting(true);
    setDeleteBlockMessage('');
    setDeleteBlockCounts(null);

    try {
      await api.delete(`/api/platform-admin/tenants/${id}`, {
        data: {
          confirm_slug: deleteSlugInput,
        },
      });

      message.success('Tenant excluido com sucesso.');
      setDeleteModalOpen(false);
      navigate('/platform');
    } catch (requestError) {
      console.error(requestError);

      const statusCode = requestError?.response?.status;
      const detail = requestError?.response?.data?.detail;

      if (statusCode === 409) {
        const blockMessage = detail?.message || 'Nao foi possivel excluir porque existem dados operacionais vinculados.';
        const counts = detail?.counts && typeof detail.counts === 'object' ? detail.counts : {};
        setDeleteBlockMessage(blockMessage);
        setDeleteBlockCounts(counts);
        message.warning(blockMessage);
      } else {
        const backendMessage =
          typeof detail === 'string'
            ? detail
            : detail?.message || 'Nao foi possivel excluir o tenant.';
        message.error(backendMessage);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateTenantAdmin = async () => {
    if (!id) {
      return;
    }

    const fullName = String(adminFormValues.full_name || '').trim();
    const email = String(adminFormValues.email || '').trim().toLowerCase();
    const temporaryPassword = String(adminFormValues.temporary_password || '');

    if (!fullName || !email || !temporaryPassword) {
      setAdminCreateError('Informe nome, email e senha temporaria.');
      return;
    }

    if (temporaryPassword.length < 8) {
      setAdminCreateError('A senha temporaria deve ter no minimo 8 caracteres.');
      return;
    }

    setAdminCreateError('');
    setCreatingAdmin(true);

    try {
      await api.post(`/api/platform-admin/tenants/${id}/admin-users`, {
        full_name: fullName,
        email,
        temporary_password: temporaryPassword,
      });

      message.success('Admin do tenant criado com sucesso.');
      setAdminFormValues({
        full_name: '',
        email: '',
        temporary_password: '',
      });
      await loadTenantAdmins();
    } catch (requestError) {
      console.error(requestError);
      const detail = requestError?.response?.data?.detail;
      setAdminCreateError(
        typeof detail === 'string' ? detail : 'Nao foi possivel criar o admin do tenant.',
      );
    } finally {
      setCreatingAdmin(false);
    }
  };

  const adminColumns = useMemo(
    () => [
      {
        title: 'Nome',
        dataIndex: 'full_name',
        key: 'full_name',
        render: (value) => value || '-',
      },
      {
        title: 'E-mail',
        dataIndex: 'email',
        key: 'email',
        render: (value) => value || '-',
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, record) => {
          const membershipStatus = String(record?.membership_status || '').toUpperCase();
          const userIsActive = Boolean(record?.is_active);

          if (membershipStatus && membershipStatus !== 'ACTIVE') {
            return membershipStatus;
          }

          return userIsActive ? 'ATIVO' : 'INATIVO';
        },
      },
      {
        title: 'Primeiro acesso pendente',
        dataIndex: 'force_password_change',
        key: 'force_password_change',
        render: (value) => (value ? 'SIM' : 'NAO'),
      },
      {
        title: 'Criado em',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => formatDate(value),
      },
    ],
    [],
  );

  const isDeleteConfirmEnabled =
    deleteSlugInput.trim() === (tenant?.slug || '') && !deleting;

  const deleteBlockEntries = deleteBlockCounts
    ? Object.entries(deleteBlockCounts)
    : [];

  if (loading) {
    return (
      <div style={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Carregando detalhe do tenant..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Detalhe do Tenant
      </Title>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar"
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Dados do tenant">
            <p>
              <Text strong>Nome: </Text>
              <Text>{tenant?.name || '-'}</Text>
            </p>
            <p>
              <Text strong>Slug: </Text>
              <Text>{tenant?.slug || '-'}</Text>
            </p>
            <p>
              <Text strong>Status: </Text>
              <Text>{tenant?.status || '-'}</Text>
            </p>
            <p>
              <Text strong>Cadastro publico: </Text>
              <Text>{tenant?.public_signup_enabled ? 'Permitido' : 'Bloqueado'}</Text>
            </p>
            <p style={{ marginBottom: 0 }}>
              <Text strong>Criado em: </Text>
              <Text>{formatDate(tenant?.created_at)}</Text>
            </p>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Dados da subscription">
            {subscription ? (
              <>
                <p>
                  <Text strong>Plano: </Text>
                  <Text>{subscription.plan_code || '-'}</Text>
                </p>
                <p>
                  <Text strong>Status: </Text>
                  <Text>{subscription.status || '-'}</Text>
                </p>
                <p>
                  <Text strong>Trial ends: </Text>
                  <Text>{formatDate(subscription.trial_ends_at)}</Text>
                </p>
                <p>
                  <Text strong>Starts_at: </Text>
                  <Text>{formatDate(subscription.starts_at)}</Text>
                </p>
                <p style={{ marginBottom: 0 }}>
                  <Text strong>Ends_at: </Text>
                  <Text>{formatDate(subscription.ends_at)}</Text>
                </p>
              </>
            ) : (
              <Text>Sem assinatura</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <PlatformTenantSubscriptionPanel tenantId={id} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 0 }}>
        <Col xs={24}>
          <Card title="Acoes Administrativas" style={{ marginTop: 16 }}>
            {saveError ? (
              <Alert
                type="error"
                showIcon
                message="Erro ao salvar"
                description={saveError}
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={6}>
                <Text strong>Status do tenant</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  options={TENANT_STATUS_OPTIONS}
                  value={formValues.tenant_status}
                  onChange={(value) => setFormValues((prev) => ({ ...prev, tenant_status: value }))}
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Text strong>Permitir cadastro publico</Text>
                <div style={{ marginTop: 8 }}>
                  <Switch
                    checked={Boolean(formValues.public_signup_enabled)}
                    onChange={(checked) =>
                      setFormValues((prev) => ({ ...prev, public_signup_enabled: Boolean(checked) }))
                    }
                    checkedChildren="Sim"
                    unCheckedChildren="Nao"
                  />
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                Salvar alteracoes
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Criar admin do tenant" style={{ marginTop: 16 }}>
            {adminCreateError ? (
              <Alert
                type="error"
                showIcon
                message="Erro ao criar admin"
                description={adminCreateError}
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Text strong>Nome do admin</Text>
                <Input
                  style={{ marginTop: 8 }}
                  value={adminFormValues.full_name}
                  onChange={(event) =>
                    setAdminFormValues((prev) => ({
                      ...prev,
                      full_name: event.target.value,
                    }))
                  }
                  placeholder="Ex.: Maria Silva"
                  disabled={creatingAdmin}
                />
              </Col>

              <Col xs={24} md={8}>
                <Text strong>Email do admin</Text>
                <Input
                  style={{ marginTop: 8 }}
                  value={adminFormValues.email}
                  onChange={(event) =>
                    setAdminFormValues((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                  placeholder="admin@tenant.com"
                  disabled={creatingAdmin}
                />
              </Col>

              <Col xs={24} md={8}>
                <Text strong>Senha temporaria</Text>
                <Input.Password
                  style={{ marginTop: 8 }}
                  value={adminFormValues.temporary_password}
                  onChange={(event) =>
                    setAdminFormValues((prev) => ({
                      ...prev,
                      temporary_password: event.target.value,
                    }))
                  }
                  placeholder="Minimo 8 caracteres"
                  disabled={creatingAdmin}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={handleCreateTenantAdmin} loading={creatingAdmin}>
                Criar admin
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Admins do tenant" style={{ marginTop: 16 }}>
            {adminsError ? (
              <Alert
                type="error"
                showIcon
                message="Erro ao carregar admins"
                description={adminsError}
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Table
              rowKey="user_id"
              loading={adminsLoading}
              columns={adminColumns}
              dataSource={tenantAdmins}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: 'Nenhum admin vinculado a este tenant.' }}
            />
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title="Zona de perigo"
            style={{ marginTop: 16, borderColor: '#ff4d4f' }}
            headStyle={{ color: '#cf1322' }}
          >
            <Text style={{ display: 'block', marginBottom: 12 }}>
              Esta acao exclui o tenant de forma permanente e nao pode ser desfeita.
            </Text>

            <Button danger onClick={openDeleteModal}>
              Excluir Tenant
            </Button>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Confirmar exclusao de tenant"
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        maskClosable={!deleting}
        okText="Excluir tenant"
        cancelText="Cancelar"
        okButtonProps={{
          danger: true,
          disabled: !isDeleteConfirmEnabled,
          loading: deleting,
        }}
        cancelButtonProps={{ disabled: deleting }}
        onOk={handleDeleteTenant}
      >
        <Alert
          type="warning"
          showIcon
          message="Acao irreversivel"
          description="A exclusao remove o tenant e os dados SaaS basicos por cascade. Confirme apenas em ambiente inicial/testes."
          style={{ marginBottom: 16 }}
        />

        <p style={{ marginBottom: 8 }}>
          <Text strong>Slug esperado: </Text>
          <Text code>{tenant?.slug || '-'}</Text>
        </p>

        <Text>Digite exatamente o slug para habilitar a confirmacao:</Text>
        <Input
          style={{ marginTop: 8 }}
          value={deleteSlugInput}
          onChange={(event) => setDeleteSlugInput(event.target.value)}
          placeholder="Digite o slug do tenant"
          disabled={deleting}
        />

        {deleteBlockMessage ? (
          <Alert
            type="error"
            showIcon
            message="Exclusao bloqueada"
            description={deleteBlockMessage}
            style={{ marginTop: 16 }}
          />
        ) : null}

        {deleteBlockEntries.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <Text strong>Contagens encontradas:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
              {deleteBlockEntries.map(([tableName, count]) => (
                <li key={tableName}>
                  <Text>{`${tableName}: ${count}`}</Text>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default PlatformTenantDetail;
